import _ from "lodash";

export const DEFAULT_AVATAR_URL =
  "https://cloud.toddleapp.com/eu-west-1/s/NA9b1N/content/rt/uploads/a6zQ8w-yE/73-730434_04-dummy-avatar.png";
export const CREATE = "create";
export const EDIT = "edit";
export const VIEW = "view";
export const ADD_COMMENT = "add_comment";
export const DELETE_COMMENT = "delete_comment";
export const UPDATE_COMMENT = "update_comment";
export const RESOLVE_CONVERSATION = "resolve_conversation";
export const UPDATE_CONVERSATION = "update_conversation";
export const ADD_DRAFT_CONVERSATION = "add_draft_conversation";
export const ACCEPT_SUGGESTION = "accept_suggestion";

export const transformConversation = (conversation, config) => {
  const { isStaffPublished = false } = config;
  const highlightType = _.get(conversation, "highlightType", "");
  const internalThreadId = _.get(conversation, "internalThreadId", "");
  const comments = _.get(conversation, "comments", []);

  // Map comments to messages
  const messages = _.map(comments, comment => {
    let state = "PUBLISHED";
    if (_.get(conversation, "isDraft")) state = "DRAFT";
    else if (isStaffPublished) state = "STAFF_ONLY";

    let authorType = "ASSISTANT";
    if (_.get(comment, "authorConfig")) authorType = "USER";

    return {
      state,
      type: _.upperCase(_.get(comment, "type", "")),
      authorType,
      content: _.get(comment, "content", ""),
    };
  });

  return {
    colorCode: highlightType,
    referenceId: internalThreadId,
    messages,
  };
};

export const mapBackendConversationsToFrontend = backendConversations => {
  return _.map(backendConversations, backendConv => {
    const messages = _.get(backendConv, "messages", []);
    const isAISuggestion =
      !_.isEmpty(messages) &&
      !_.some(messages, msg => _.get(msg, "authorType") === "USER");
    const justification = _.find(
      messages,
      msg => _.get(msg, "type") === "JUSTIFICATION"
    );
    // Note: Keeping this for future reference if we need justification
    // const isJustificationAvailable = !_.isEmpty(justification);

    const isJustificationAvailable = false;
    const comments = _.map(
      _.filter(messages, msg => _.get(msg, "type") !== "JUSTIFICATION"),
      msg => ({
        authorConfig: isAISuggestion
          ? {}
          : {
              id: _.get(msg, "createdBy.id", ""),
              name: `${_.get(msg, "createdBy.firstName", "")} ${_.get(
                msg,
                "createdBy.lastName",
                ""
              )}`.trim(),
              avatarURL: !_.isEmpty(_.get(msg, "createdBy.profileImage"))
                ? _.get(msg, "createdBy.profileImage")
                : "",
            },
        content: _.get(msg, "content", ""),
        isDeleted: false,
        internalCommentId: _.get(msg, "id", ""),
        timeStamp: _.get(msg, "createdAt"),
        type: _.toLower(_.get(msg, "type", "")),
      })
    );

    const updatedComments = isAISuggestion ? [_.get(comments, "0")] : comments;

    return {
      comments: updatedComments,
      id: _.get(backendConv, "id", ""),
      internalThreadId: _.get(backendConv, "referenceId", ""),
      quote: "",
      type: "thread",
      isResolved: _.get(backendConv, "isResolved", false),
      isDeleted: false,
      isAISuggestion,
      isSuggested: isAISuggestion,
      isJustificationAvailable,
      justificationConfig: isJustificationAvailable
        ? {
            justification: _.get(justification, "content"),
            justificationType: null,
          }
        : undefined,
      isDraft:
        !isAISuggestion &&
        _.get(backendConv, "derivedThreadState", "") === "DRAFT",
      highlightType: _.get(backendConv, "colorCode", ""),
      createdBy: _.get(_.first(messages), "createdBy.id", ""),
    };
  });
};

const getMessageIds = comments => _.map(comments, "internalCommentId");

export const transformConversationForUpdate = (oldConv, newConv, config) => {
  const { isStaffPublished = false, isAiSuggestionAccepted = false } = config;

  // Helper to compute the outgoing message state for ToddleDoc messages
  const getToddleDocMessageState = ({
    messageState,
    convIsDraft,
    isStaffPublishedFlag,
  }) => {
    if (messageState) return messageState;
    if (convIsDraft) return "DRAFT";
    return isStaffPublishedFlag ? "STAFF_ONLY" : "PUBLISHED";
  };

  const oldComments = _.get(oldConv, "comments", []);
  const newComments = _.get(newConv, "comments", []);

  // Helper to get message IDs from comments array

  // Find removed messages (present in old, not in new)
  const removedIds = _.difference(
    getMessageIds(oldComments),
    getMessageIds(newComments)
  );

  // Find added messages (present in new, not in old)
  const addedIds = _.difference(
    getMessageIds(newComments),
    getMessageIds(oldComments)
  );
  const addedMessages = _.filter(newComments, c =>
    _.includes(addedIds, c.internalCommentId)
  );

  // Find updated messages (present in both, but content/state/type changed)
  const updatedMessages = _.filter(newComments, c => {
    const old = _.find(oldComments, { internalCommentId: c.internalCommentId });
    return old && (c.content !== old.content || c.state !== old.state);
  });

  // Find messages that are marked deleted in new, but not in old
  const deletedMessages = _.filter(newComments, c => {
    if (!c.isDeleted) return false;
    const old = _.find(oldComments, { internalCommentId: c.internalCommentId });
    return old && !old.isDeleted;
  });
  const deletedIds = _.map(deletedMessages, "internalCommentId");

  // Combine removedIds and deletedIds
  const removedToddleDocMessage = _.uniq([...removedIds, ...deletedIds]);

  // Format updated messages for BE
  const updatedToddleDocMessage = _.map(updatedMessages, c => ({
    id: c.internalCommentId,
    state: getToddleDocMessageState({
      messageState: c.state,
      convIsDraft: _.get(newConv, "isDraft"),
      isStaffPublishedFlag: isStaffPublished,
    }),
    content: c.content,
  }));

  // Format added messages for BE
  const addedToddleDocMessage = _.map(addedMessages, c => ({
    content: c.content,
    type: _.upperCase(c.type || "COMMENT"),
    state: getToddleDocMessageState({
      messageState: _.get(c, "isDraft") ? "DRAFT" : undefined,
      convIsDraft: false,
      isStaffPublishedFlag: isStaffPublished,
    }),
    authorType: "USER",
  }));

  // Draft → STAFF_ONLY transition when conversation is published
  const oldIsDraft = _.get(oldConv, "isDraft");
  const newIsDraft = _.get(newConv, "isDraft");
  const firstComment = _.get(newComments, "[0]");

  if (
    (oldIsDraft === true && newIsDraft === false && firstComment) ||
    isAiSuggestionAccepted
  ) {
    const existingIndex = _.findIndex(
      updatedToddleDocMessage,
      msg => msg.id === firstComment.internalCommentId
    );

    if (existingIndex !== -1) {
      // If already exists, just update state (and content if required)
      updatedToddleDocMessage[existingIndex] = {
        ...updatedToddleDocMessage[existingIndex],
        state: getToddleDocMessageState({
          messageState: undefined,
          convIsDraft: false,
          isStaffPublishedFlag: isStaffPublished,
        }),
      };
    } else {
      //  Else push a new update
      updatedToddleDocMessage.push({
        id: firstComment.internalCommentId,
        state: getToddleDocMessageState({
          messageState: undefined,
          convIsDraft: false,
          isStaffPublishedFlag: isStaffPublished,
        }),
      });
    }
  }

  const output = {
    id: _.get(oldConv, "id", ""),
    messages: {
      updatedToddleDocMessage,
      removedToddleDocMessage,
      addedToddleDocMessage,
    },
  };

  // Only include isResolved if changed
  if (_.get(oldConv, "isResolved") !== _.get(newConv, "isResolved")) {
    output.isResolved = _.get(newConv, "isResolved", undefined);
  }

  // Only include colorCode if changed
  if (_.get(oldConv, "highlightType") !== _.get(newConv, "highlightType")) {
    output.colorCode = _.get(newConv, "highlightType");
  }

  return output;
};

export const generateConversationsHash = conversations => {
  if (!_.isArray(conversations) || _.isEmpty(conversations)) {
    return "empty";
  }

  // Create a comprehensive signature including all relevant fields
  const conversationSignature = JSON.stringify(
    _.map(conversations, c => ({
      id: c.id,
      internalThreadId: c.internalThreadId,
      isResolved: c.isResolved,
      isDraft: c.isDraft,
      isAISuggestion: c.isAISuggestion,
      highlightType: c.highlightType,
      commentCount: _.size(c.comments),
      comments: _.map(c.comments, comment => ({
        id: comment.internalCommentId,
        content: comment.content,
        state: comment.state,
        timeStamp: comment.timeStamp,
        isDraft: comment.isDraft,
        isAISuggestion: comment.isAISuggestion,
      })),
      lastModified: _.get(_.last(c.comments), "timeStamp"),
    }))
  );

  // Generate simple hash from signature
  return generateSimpleHash(conversationSignature);
};

const generateSimpleHash = str => {
  if (!str) return "0";

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  return Math.abs(hash).toString(36);
};

export const getFilterDropdownOptions = t => {
  return [
    { key: "ACTIVE", label: t("common:active"), value: "ACTIVE" },
    {
      key: "RESOLVED",
      label: t("common:resolved"),
      value: "RESOLVED",
    },
    {
      key: "HIDDEN",
      label: t("classRoom:hide_all"),
      value: "HIDDEN",
    },
  ];
};

export const getLabValueMap = t => ({
  ACTIVE: t("common:active"),
  RESOLVED: t("common:resolved"),
  HIDDEN: t("classRoom:hide_all"),
});
